/**
 * Analytics Dashboard for Ragsmith Web UI
 * Full-page modal with LLM usage analytics, cost estimation, and free tier tracking
 */

import { fetchModelUsage, fetchRateLimits, fetchModelPricing, fetchToolUsage, resetUsageData, fetchContainers } from './api.js';
import { escapeHtml } from './html-escape.js';
import { isContainerMode } from './state.js';

// ─── State ──────────────────────────────────────────────────────────────────

let modalOpen = false;
let currentPeriod = 'week';
let currentContainer = '';  // '' = admin (self), token string = specific container
let usageData = null;
let rateLimits = {};
let pricing = {};
let pricingLoaded = false;
let toolUsageData = null;

// ─── DOM References ─────────────────────────────────────────────────────────

let els = {};

// ─── Provider Config ────────────────────────────────────────────────────────

const PROVIDER_COLORS = {
    local: '#10b981',
    anthropic: '#a78bfa',
    openai: '#10b981',
    groq: '#f59e0b',
    openrouter: '#3b82f6'
};

const PROVIDER_LABELS = {
    local: 'Local (Ollama)',
    anthropic: 'Anthropic',
    openai: 'OpenAI',
    groq: 'Groq',
    openrouter: 'OpenRouter'
};

const CALL_TYPE_LABELS = {
    chat: 'Chat',
    tool_loop: 'Tool Loop',
    memory_extraction: 'Memory Extraction',
    citation: 'Citations',
    web_search_reformulate: 'Search (Reformulate)',
    web_search_select: 'Search (Select)',
    web_search_summary: 'Search (Summary)'
};

const CALL_TYPE_COLORS = {
    chat: '#3b82f6',
    tool_loop: '#f59e0b',
    memory_extraction: '#a78bfa',
    citation: '#10b981',
    web_search_reformulate: '#06b6d4',
    web_search_select: '#06b6d4',
    web_search_summary: '#06b6d4'
};

const TOOL_LABELS = {
    web_search: 'Web Search',
    web_fetch: 'Web Fetch',
    rag_search: 'Doc Search',
    file_ops: 'File Ops',
    shell_exec: 'Shell',
    python_exec: 'Python',
    calculator: 'Calculator',
    datetime: 'Date/Time',
    agent_db: 'Database',
    http_request: 'HTTP',
    email_send: 'Email'
};

const TOOL_COLORS = {
    web_search: '#3b82f6',
    web_fetch: '#06b6d4',
    http_request: '#10b981',
    email_send: '#f97316',
    rag_search: '#10b981',
    file_ops: '#f59e0b',
    shell_exec: '#ef4444',
    python_exec: '#8b5cf6',
    calculator: '#ec4899',
    datetime: '#6b7280',
    agent_db: '#14b8a6'
};

// ─── Modal HTML ─────────────────────────────────────────────────────────────

function createModalHTML() {
    const html = `
        <div id="analytics-overlay" class="modal-overlay"></div>
        <div id="analytics-panel" class="analytics-panel">
            <div class="analytics-header">
                <h2>Analytics</h2>
                <div class="analytics-header-right">
                    <select id="analytics-container-select" class="analytics-container-select" style="display:none">
                        <option value="">My Usage</option>
                    </select>
                    <div class="analytics-period-tabs" id="analytics-period-tabs">
                        <button class="period-tab" data-period="today">Today</button>
                        <button class="period-tab active" data-period="week">7 Days</button>
                        <button class="period-tab" data-period="month">30 Days</button>
                        <button class="period-tab" data-period="all">All Time</button>
                    </div>
                    <button id="analytics-close" class="analytics-close-btn">&times;</button>
                </div>
            </div>
            <div class="analytics-body" id="analytics-body">
                <div class="analytics-section" id="analytics-token-limits-section" style="display:none">
                    <div id="analytics-token-limits" class="analytics-token-limits"></div>
                </div>
                <div class="analytics-section">
                    <div class="analytics-cards" id="analytics-overview-cards"></div>
                </div>
                <div class="analytics-section">
                    <h3 class="analytics-section-title">Provider Breakdown</h3>
                    <div id="analytics-provider-bars" class="analytics-bar-chart"></div>
                </div>
                <div class="analytics-section" id="analytics-cost-section">
                    <h3 class="analytics-section-title">Cost Estimator</h3>
                    <div id="analytics-cost-content" class="analytics-cost-content"></div>
                </div>
                <div class="analytics-section" id="analytics-free-tier-section">
                    <h3 class="analytics-section-title">Free Tier Status</h3>
                    <div id="analytics-free-tier-content" class="analytics-free-tier"></div>
                </div>
                <div class="analytics-section">
                    <h3 class="analytics-section-title">Model Leaderboard</h3>
                    <div id="analytics-leaderboard" class="analytics-leaderboard"></div>
                </div>
                <div class="analytics-section">
                    <h3 class="analytics-section-title">Usage by Call Type</h3>
                    <div id="analytics-call-types" class="analytics-bar-chart"></div>
                </div>
                <div class="analytics-section" id="analytics-tool-usage-section">
                    <h3 class="analytics-section-title">Tool Usage</h3>
                    <div id="analytics-tool-usage" class="analytics-bar-chart"></div>
                </div>
                <div class="analytics-note" id="analytics-note"></div>
            </div>
        </div>
    `;
    document.body.insertAdjacentHTML('beforeend', html);
}

// ─── Init & Open/Close ──────────────────────────────────────────────────────

export function initAnalytics() {
    createModalHTML();

    els = {
        overlay: document.getElementById('analytics-overlay'),
        panel: document.getElementById('analytics-panel'),
        closeBtn: document.getElementById('analytics-close'),
        periodTabs: document.getElementById('analytics-period-tabs'),
        overviewCards: document.getElementById('analytics-overview-cards'),
        providerBars: document.getElementById('analytics-provider-bars'),
        costContent: document.getElementById('analytics-cost-content'),
        costSection: document.getElementById('analytics-cost-section'),
        freeTierContent: document.getElementById('analytics-free-tier-content'),
        freeTierSection: document.getElementById('analytics-free-tier-section'),
        leaderboard: document.getElementById('analytics-leaderboard'),
        callTypes: document.getElementById('analytics-call-types'),
        toolUsage: document.getElementById('analytics-tool-usage'),
        toolUsageSection: document.getElementById('analytics-tool-usage-section'),
        note: document.getElementById('analytics-note'),
        tokenLimits: document.getElementById('analytics-token-limits'),
        tokenLimitsSection: document.getElementById('analytics-token-limits-section'),
    };

    els.containerSelect = document.getElementById('analytics-container-select');

    els.overlay?.addEventListener('click', closeAnalytics);
    els.closeBtn?.addEventListener('click', closeAnalytics);
    els.periodTabs?.addEventListener('click', handlePeriodClick);
    els.containerSelect?.addEventListener('change', handleContainerChange);

    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && modalOpen) closeAnalytics();
    });
}

export async function openAnalytics() {
    if (modalOpen) return;
    modalOpen = true;

    els.overlay?.classList.add('active');
    els.panel?.classList.add('open');
    document.body.style.overflow = 'hidden';

    els.overviewCards.innerHTML = '<div class="analytics-loading">Loading analytics...</div>';
    // Clear other sections while loading
    els.providerBars.innerHTML = '';
    els.costContent.innerHTML = '';
    els.freeTierContent.innerHTML = '';
    els.leaderboard.innerHTML = '';
    els.callTypes.innerHTML = '';
    els.toolUsage.innerHTML = '';
    els.note.innerHTML = '';

    // Populate container selector for admin users (not in container mode)
    if (!isContainerMode() && els.containerSelect) {
        try {
            const data = await fetchContainers();
            const containers = data.containers || [];
            if (containers.length > 0) {
                els.containerSelect.style.display = '';
                els.containerSelect.innerHTML = '<option value="">Total Usage</option>';
                for (const c of containers) {
                    const label = c.description || `#${c.id} ${c.mode}`;
                    const convos = c.conversation_count ? ` (${c.conversation_count} convos)` : '';
                    els.containerSelect.innerHTML += `<option value="${c.token}">${label}${convos}</option>`;
                }
                // Restore selection
                if (currentContainer) els.containerSelect.value = currentContainer;
            } else {
                els.containerSelect.style.display = 'none';
            }
        } catch (e) {
            els.containerSelect.style.display = 'none';
        }
    } else if (els.containerSelect) {
        els.containerSelect.style.display = 'none';
    }

    try {
        const viewToken = currentContainer || null;
        const fetches = [
            fetchModelUsage(currentPeriod, null, viewToken),
            fetchRateLimits(),
            fetchToolUsage(currentPeriod, viewToken),
        ];
        if (!pricingLoaded) {
            fetches.push(fetchModelPricing());
        }

        const results = await Promise.all(fetches);
        usageData = results[0];
        rateLimits = results[1].rate_limits || {};
        toolUsageData = results[2];
        if (!pricingLoaded && results[3]) {
            pricing = results[3].pricing || {};
            pricingLoaded = true;
        }

        renderAll();
    } catch (e) {
        console.error('[Analytics] Load failed:', e);
        els.overviewCards.innerHTML = '<div class="analytics-error">Failed to load analytics data</div>';
    }
}

function closeAnalytics() {
    modalOpen = false;
    els.overlay?.classList.remove('active');
    els.panel?.classList.remove('open');
    document.body.style.overflow = '';
}

// ─── Period Tabs ────────────────────────────────────────────────────────────

function handlePeriodClick(e) {
    const btn = e.target.closest('.period-tab');
    if (!btn) return;

    const period = btn.dataset.period;
    if (period === currentPeriod) return;

    currentPeriod = period;
    els.periodTabs.querySelectorAll('.period-tab').forEach(t => t.classList.remove('active'));
    btn.classList.add('active');

    refreshData();
}

async function refreshData() {
    try {
        const viewToken = currentContainer || null;
        const [usage, tools] = await Promise.all([
            fetchModelUsage(currentPeriod, null, viewToken),
            fetchToolUsage(currentPeriod, viewToken),
        ]);
        usageData = usage;
        toolUsageData = tools;
        renderAll();
    } catch (e) {
        console.error('[Analytics] Refresh failed:', e);
    }
}

function handleContainerChange(e) {
    currentContainer = e.target.value;
    refreshData();
}

// ─── Render All ─────────────────────────────────────────────────────────────

function renderAll() {
    renderTokenLimits();
    renderOverviewCards();
    renderProviderBreakdown();
    renderCostEstimator();
    renderFreeTierStatus();
    renderModelLeaderboard();
    renderCallTypeBreakdown();
    renderToolUsage();
    renderDataNote();
}

// ─── Token Limits (Container) ──────────────────────────────────────────────

function renderTokenLimits() {
    const limits = usageData?.token_limits;
    if (!limits || (!limits.daily_limit && !limits.weekly_limit)) {
        els.tokenLimitsSection.style.display = 'none';
        return;
    }

    els.tokenLimitsSection.style.display = '';
    let html = '';

    if (limits.daily_limit) {
        const used = limits.today_tokens || 0;
        const cap = limits.daily_limit;
        const pct = Math.min((used / cap) * 100, 100);
        const color = pct < 50 ? '#10b981' : (pct < 90 ? '#f59e0b' : '#ef4444');
        const remaining = Math.max(cap - used, 0);

        html += `
            <div class="token-limit-card">
                <div class="token-limit-header">
                    <span class="token-limit-label">Daily Token Usage</span>
                    <span class="token-limit-count">${formatTokens(used)} / ${formatTokens(cap)}</span>
                </div>
                <div class="token-limit-bar-track">
                    <div class="token-limit-bar-fill" style="width:${pct}%;background:${color}"></div>
                </div>
                <div class="token-limit-stats">
                    <span>${formatTokens(remaining)} remaining</span>
                    <span class="token-limit-pct">${Math.round(pct)}%</span>
                </div>
            </div>
        `;
    }

    if (limits.weekly_limit) {
        const used = limits.week_tokens || 0;
        const cap = limits.weekly_limit;
        const pct = Math.min((used / cap) * 100, 100);
        const color = pct < 50 ? '#10b981' : (pct < 90 ? '#f59e0b' : '#ef4444');
        const remaining = Math.max(cap - used, 0);

        html += `
            <div class="token-limit-card">
                <div class="token-limit-header">
                    <span class="token-limit-label">Weekly Token Usage</span>
                    <span class="token-limit-count">${formatTokens(used)} / ${formatTokens(cap)}</span>
                </div>
                <div class="token-limit-bar-track">
                    <div class="token-limit-bar-fill" style="width:${pct}%;background:${color}"></div>
                </div>
                <div class="token-limit-stats">
                    <span>${formatTokens(remaining)} remaining</span>
                    <span class="token-limit-pct">${Math.round(pct)}%</span>
                </div>
            </div>
        `;
    }

    els.tokenLimits.innerHTML = html;
}

// ─── Overview Cards ─────────────────────────────────────────────────────────

function renderOverviewCards() {
    const summary = usageData?.summary || {};
    const totalRequests = summary.total_requests || 0;
    const totalPrompt = summary.total_prompt_tokens || 0;
    const totalCompletion = summary.total_completion_tokens || 0;
    const totalTokens = totalPrompt + totalCompletion;
    const modelsUsed = summary.models_used || 0;
    const totalCost = calculateTotalCost();

    els.overviewCards.innerHTML = `
        <div class="analytics-stat-card">
            <div class="stat-value">${formatNumber(totalRequests)}</div>
            <div class="stat-label">Total Requests</div>
            <div class="stat-sub">${formatNumber(summary.total_chats || 0)} chats</div>
        </div>
        <div class="analytics-stat-card">
            <div class="stat-value">${formatTokens(totalTokens)}</div>
            <div class="stat-label">Total Tokens</div>
            <div class="stat-sub">${formatTokens(totalPrompt)} in / ${formatTokens(totalCompletion)} out</div>
        </div>
        <div class="analytics-stat-card accent-cost">
            <div class="stat-value">${formatCost(totalCost)}</div>
            <div class="stat-label">Estimated Cost</div>
            <div class="stat-sub">Based on listed pricing</div>
        </div>
        <div class="analytics-stat-card">
            <div class="stat-value">${modelsUsed}</div>
            <div class="stat-label">Models Used</div>
            <div class="stat-sub">${periodLabel()}</div>
        </div>
    `;
}

// ─── Provider Breakdown ─────────────────────────────────────────────────────

function renderProviderBreakdown() {
    const byProvider = usageData?.by_provider || {};
    if (Object.keys(byProvider).length === 0) {
        els.providerBars.innerHTML = '<div class="analytics-empty">No usage data</div>';
        return;
    }

    const maxRequests = Math.max(...Object.values(byProvider).map(p => p.request_count));

    let html = '';
    for (const [provider, stats] of Object.entries(byProvider)) {
        const pct = maxRequests > 0 ? (stats.request_count / maxRequests) * 100 : 0;
        const color = PROVIDER_COLORS[provider] || '#666';
        const totalTokens = (stats.prompt_tokens || 0) + (stats.completion_tokens || 0);
        html += `
            <div class="bar-row">
                <div class="bar-label">
                    <span class="bar-dot" style="background:${color}"></span>
                    ${PROVIDER_LABELS[provider] || provider}
                </div>
                <div class="bar-track">
                    <div class="bar-fill" style="width:${pct}%;background:${color}"></div>
                </div>
                <div class="bar-value">${formatNumber(stats.request_count)} <span class="bar-token-count">${formatTokens(totalTokens)}</span></div>
            </div>
        `;
    }
    els.providerBars.innerHTML = html;
}

// ─── Cost Estimator ─────────────────────────────────────────────────────────

function calculateModelCost(modelId, promptTokens, completionTokens, modelName) {
    // Try pricing by tier name first, then by actual model name
    const p = pricing[modelId] || (modelName ? pricing[modelName] : null);
    if (!p) return 0;
    return (promptTokens * p.input + completionTokens * p.output) / 1_000_000;
}

function calculateTotalCost() {
    const models = usageData?.models || {};
    let total = 0;
    for (const [, stats] of Object.entries(models)) {
        total += calculateModelCost(stats.model_id, stats.prompt_tokens || 0, stats.completion_tokens || 0, stats.model_name);
    }
    return total;
}

function renderCostEstimator() {
    const models = usageData?.models || {};
    const costEntries = [];

    for (const [, stats] of Object.entries(models)) {
        const cost = calculateModelCost(stats.model_id, stats.prompt_tokens || 0, stats.completion_tokens || 0, stats.model_name);
        costEntries.push({
            modelId: stats.model_id,
            modelName: stats.model_name || null,
            provider: stats.provider,
            cost,
            promptTokens: stats.prompt_tokens || 0,
            completionTokens: stats.completion_tokens || 0,
            requests: stats.request_count || 0,
        });
    }

    costEntries.sort((a, b) => b.cost - a.cost);

    const paid = costEntries.filter(e => e.cost > 0);
    const free = costEntries.filter(e => e.cost === 0);

    if (paid.length === 0 && free.length === 0) {
        els.costSection.style.display = 'none';
        return;
    }

    els.costSection.style.display = '';
    let html = '';

    if (paid.length > 0) {
        html += '<div class="cost-table">';
        html += '<div class="cost-header"><span>Model</span><span>Tokens</span><span>Cost</span></div>';
        for (const entry of paid) {
            const totalTokens = entry.promptTokens + entry.completionTokens;
            html += `
                <div class="cost-row">
                    <span class="cost-model">${formatModelDisplay(entry.modelId, entry.modelName)}</span>
                    <span class="cost-tokens">${formatTokens(totalTokens)}</span>
                    <span class="cost-amount">${formatCost(entry.cost)}</span>
                </div>
            `;
        }
        const totalPaid = paid.reduce((sum, e) => sum + e.cost, 0);
        html += `
            <div class="cost-row cost-total">
                <span>Total Paid</span>
                <span></span>
                <span class="cost-amount">${formatCost(totalPaid)}</span>
            </div>
        `;
        html += '</div>';
    }

    if (free.length > 0) {
        const freeTokens = free.reduce((sum, e) => sum + e.promptTokens + e.completionTokens, 0);
        html += `
            <div class="cost-free-summary">
                <span class="cost-free-badge">Free</span>
                ${free.length} free model${free.length > 1 ? 's' : ''} used
                (${formatTokens(freeTokens)} tokens, $0.00)
            </div>
        `;
    }

    els.costContent.innerHTML = html;
}

// ─── Free Tier Status ───────────────────────────────────────────────────────

function renderFreeTierStatus() {
    let html = '';
    let hasAnyLimits = false;

    const providers = [
        { key: 'openrouter', label: 'OpenRouter', color: '#3b82f6', defaultTotal: 50 },
        { key: 'groq', label: 'Groq', color: '#f59e0b', defaultTotal: 1000 },
    ];

    for (const prov of providers) {
        if (!rateLimits[prov.key]) continue;
        hasAnyLimits = true;

        const rl = rateLimits[prov.key];
        const remaining = rl.limit_remaining ?? 0;
        const total = rl.limit_total ?? prov.defaultTotal;
        const pct = total > 0 ? (remaining / total) * 100 : 0;
        const color = pct > 50 ? '#10b981' : (pct > 10 ? '#f59e0b' : '#ef4444');

        let resetText = '';
        if (rl.reset_timestamp) {
            const resetDate = new Date(rl.reset_timestamp * 1000);
            const now = new Date();
            if (resetDate > now) {
                const hours = Math.ceil((resetDate - now) / (1000 * 60 * 60));
                resetText = hours > 24
                    ? `Resets ${resetDate.toLocaleDateString()}`
                    : `Resets in ${hours}h`;
            }
        }

        html += `
            <div class="free-tier-card">
                <div class="free-tier-header">
                    <span class="free-tier-provider" style="color:${prov.color}">${prov.label}</span>
                    <span class="free-tier-reset">${resetText}</span>
                </div>
                <div class="free-tier-bar-track">
                    <div class="free-tier-bar-fill" style="width:${pct}%;background:${color}"></div>
                </div>
                <div class="free-tier-stats">
                    <span>${remaining} / ${total} requests remaining</span>
                    <span class="free-tier-pct">${Math.round(pct)}%</span>
                </div>
            </div>
        `;
    }

    if (!hasAnyLimits) {
        els.freeTierSection.style.display = 'none';
        return;
    }

    els.freeTierSection.style.display = '';
    els.freeTierContent.innerHTML = html;
}

// ─── Model Leaderboard ──────────────────────────────────────────────────────

function formatModelDisplay(tierId, modelName) {
    // Show tier name with actual model in parentheses
    const cleanName = modelName
        ? (modelName.includes('/') ? modelName.split('/').pop() : modelName)
        : null;
    if (cleanName && cleanName !== tierId) {
        return `${escapeHtml(tierId)} <span class="model-actual">(${escapeHtml(cleanName)})</span>`;
    }
    return escapeHtml(tierId);
}

function renderModelLeaderboard() {
    const models = usageData?.models || {};
    const entries = Object.entries(models)
        .map(([, stats]) => ({
            id: stats.model_id,
            provider: stats.provider,
            modelName: stats.model_name || null,
            requests: stats.request_count || 0,
            promptTokens: stats.prompt_tokens || 0,
            completionTokens: stats.completion_tokens || 0,
            cost: calculateModelCost(stats.model_id, stats.prompt_tokens || 0, stats.completion_tokens || 0, stats.model_name)
        }))
        .sort((a, b) => b.requests - a.requests);

    if (entries.length === 0) {
        els.leaderboard.innerHTML = '<div class="analytics-empty">No model usage recorded</div>';
        return;
    }

    let html = '<div class="leaderboard-table">';
    html += '<div class="leaderboard-header"><span>#</span><span>Model</span><span>Requests</span><span>Tokens</span><span>Cost</span></div>';

    for (let i = 0; i < entries.length; i++) {
        const entry = entries[i];
        const color = PROVIDER_COLORS[entry.provider] || '#666';
        const totalTokens = entry.promptTokens + entry.completionTokens;
        html += `
            <div class="leaderboard-row">
                <span class="leaderboard-rank">${i + 1}</span>
                <span class="leaderboard-model">
                    <span class="provider-badge-sm" style="background:${color}">${PROVIDER_LABELS[entry.provider] || entry.provider}</span>
                    ${formatModelDisplay(entry.id, entry.modelName)}
                </span>
                <span class="leaderboard-requests">${formatNumber(entry.requests)}</span>
                <span class="leaderboard-tokens">${formatTokens(totalTokens)}</span>
                <span class="leaderboard-cost">${entry.cost > 0 ? formatCost(entry.cost) : '<span class="free-label">Free</span>'}</span>
            </div>
        `;
    }

    html += '</div>';
    els.leaderboard.innerHTML = html;
}

// ─── Call Type Breakdown ────────────────────────────────────────────────────

function renderCallTypeBreakdown() {
    const byCallType = usageData?.by_call_type || {};
    if (Object.keys(byCallType).length === 0) {
        els.callTypes.innerHTML = '<div class="analytics-empty">No call type data</div>';
        return;
    }

    const getTokens = (c) => (c.prompt_tokens || 0) + (c.completion_tokens || 0);
    const maxTokens = Math.max(...Object.values(byCallType).map(getTokens));
    const sorted = Object.entries(byCallType).sort((a, b) => getTokens(b[1]) - getTokens(a[1]));

    let html = '';
    for (const [callType, stats] of sorted) {
        const totalTokens = getTokens(stats);
        const pct = maxTokens > 0 ? (totalTokens / maxTokens) * 100 : 0;
        const color = CALL_TYPE_COLORS[callType] || '#666';
        const label = CALL_TYPE_LABELS[callType] || callType;
        html += `
            <div class="bar-row">
                <div class="bar-label">${escapeHtml(label)}</div>
                <div class="bar-track">
                    <div class="bar-fill" style="width:${pct}%;background:${color}"></div>
                </div>
                <div class="bar-value">${formatNumber(stats.request_count)} <span class="bar-token-count">${formatTokens(totalTokens)}</span></div>
            </div>
        `;
    }
    els.callTypes.innerHTML = html;
}

// ─── Tool Usage ─────────────────────────────────────────────────────────────

function renderToolUsage() {
    const tools = toolUsageData?.tools;
    if (!tools || Object.keys(tools).length === 0) {
        els.toolUsageSection.style.display = 'none';
        return;
    }
    els.toolUsageSection.style.display = '';

    const totalCalls = toolUsageData.total_calls || 0;
    const totalFailed = toolUsageData.total_failed || 0;

    // Sort by total calls (successful + failed) descending
    const sorted = Object.entries(tools).sort((a, b) => {
        const aTotal = a[1].calls + a[1].failed;
        const bTotal = b[1].calls + b[1].failed;
        return bTotal - aTotal;
    });

    const maxCalls = sorted.length > 0 ? sorted[0][1].calls + sorted[0][1].failed : 0;

    let html = `<div class="tool-usage-summary">
        <span class="tool-usage-total">${formatNumber(totalCalls)} total calls</span>
        ${totalFailed > 0 ? `<span class="tool-usage-failed">${totalFailed} failed</span>` : ''}
    </div>`;

    for (const [toolName, stats] of sorted) {
        const total = stats.calls + stats.failed;
        const pct = maxCalls > 0 ? (total / maxCalls) * 100 : 0;
        const color = TOOL_COLORS[toolName] || '#6b7280';
        const label = TOOL_LABELS[toolName] || toolName.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        const tokens = (stats.prompt_tokens || 0) + (stats.completion_tokens || 0);
        const failedBadge = stats.failed > 0
            ? ` <span class="tool-failed-badge">${stats.failed} failed</span>`
            : '';

        html += `
            <div class="bar-row">
                <div class="bar-label">
                    <span class="tool-dot" style="background:${color}"></span>
                    ${escapeHtml(label)}
                </div>
                <div class="bar-track">
                    <div class="bar-fill" style="width:${pct}%;background:${color}"></div>
                </div>
                <div class="bar-value">${formatNumber(total)}${failedBadge} ${tokens > 0 ? `<span class="bar-token-count">${formatTokens(tokens)}</span>` : ''}</div>
            </div>
        `;
    }

    els.toolUsage.innerHTML = html;
}

// ─── Data Note ──────────────────────────────────────────────────────────────

function renderDataNote() {
    const resetBtn = isContainerMode() ? '' : '<button id="analytics-reset-btn" class="analytics-reset-btn">Reset All Data</button>';
    els.note.innerHTML = `
        <p class="analytics-note-text">
            <strong>Note:</strong> Token counts for streaming chat responses may be underreported.
            Non-streaming calls (tool loops, web search, memory extraction, citations) have accurate token tracking.
        </p>
        ${resetBtn}
    `;
    if (!isContainerMode()) {
        document.getElementById('analytics-reset-btn')?.addEventListener('click', handleReset);
    }
}

function handleReset() {
    // Show inline confirmation UI
    const note = els.note;
    const existing = note.querySelector('.analytics-reset-confirm');
    if (existing) return; // Already showing

    const confirm = document.createElement('div');
    confirm.className = 'analytics-reset-confirm';
    confirm.innerHTML = `
        <div class="reset-confirm-message">
            <strong>Reset all analytics data?</strong>
            <p>This will permanently delete all model usage, token counts, cost history, and tool usage records. This cannot be undone.</p>
        </div>
        <div class="reset-confirm-actions">
            <button class="reset-confirm-cancel">Cancel</button>
            <button class="reset-confirm-delete">Delete All Data</button>
        </div>
    `;

    note.querySelector('.analytics-reset-btn').style.display = 'none';
    note.appendChild(confirm);

    // Scroll the modal body to bottom to reveal confirmation
    const body = document.getElementById('analytics-body');
    requestAnimationFrame(() => {
        body?.scrollTo({ top: body.scrollHeight, behavior: 'smooth' });
    });

    confirm.querySelector('.reset-confirm-cancel').addEventListener('click', () => {
        confirm.remove();
        note.querySelector('.analytics-reset-btn').style.display = '';
    });

    confirm.querySelector('.reset-confirm-delete').addEventListener('click', async () => {
        const deleteBtn = confirm.querySelector('.reset-confirm-delete');
        deleteBtn.textContent = 'Deleting...';
        deleteBtn.disabled = true;

        try {
            await resetUsageData();
            confirm.remove();
            const [usage, tools] = await Promise.all([
                fetchModelUsage(currentPeriod),
                fetchToolUsage(currentPeriod),
            ]);
            usageData = usage;
            toolUsageData = tools;
            renderAll();
        } catch (e) {
            console.error('[Analytics] Reset failed:', e);
            deleteBtn.textContent = 'Failed — try again';
            deleteBtn.disabled = false;
        }
    });
}

// ─── Utilities ──────────────────────────────────────────────────────────────

function formatNumber(n) {
    if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
    if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K';
    return n.toString();
}

function formatTokens(n) {
    if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M tok';
    if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K tok';
    return n + ' tok';
}

function formatCost(dollars) {
    if (dollars === 0) return '$0.00';
    if (dollars < 10) {
        // Show 3+ decimal places with sub-cent digits smaller
        const str = dollars < 1 ? dollars.toFixed(4) : dollars.toFixed(3);
        const parts = str.split('.');
        const cents = parts[1].slice(0, 2);
        const sub = parts[1].slice(2);
        if (sub && parseInt(sub) > 0) {
            return `$${parts[0]}.${cents}<span class="cost-sub">${sub}</span>`;
        }
        return `$${parts[0]}.${cents}`;
    }
    return '$' + dollars.toFixed(2);
}

function periodLabel() {
    const labels = { today: 'today', week: 'last 7 days', month: 'last 30 days', all: 'all time' };
    return labels[currentPeriod] || currentPeriod;
}


